<script lang="ts" context="module">
    export type Slide = {
        work: string;
        name: string;
        headline?: string;
        person_img: string;
    };
</script>

<script lang="ts">
    import Animate from "./Animate.svelte";
    import Container from "./Container.svelte";
    import DividerWave from "./DividerWave.svelte";
    import { Swiper, SwiperSlide } from "swiper/svelte";
    import { Navigation, Keyboard, Autoplay } from "swiper";
    import "swiper/css";
    import "swiper/css/navigation";

    export let divcolor: string = "var(--grayDark)";
    export let headline: string = "";
    export let subline: string = "";
    export let center: boolean = true;
    export let small: boolean = false;
    export let verysmall: boolean = false;
    export let overlay: string | boolean = false;
    export let img: string = "";
    export let vid: string = "";
    export let yellow: boolean = false;
    export let gray: boolean = false;
    export let bgpos: string = "center";
    export let animate_headline = false;
    export let loop: boolean = true;
    export let slides: Slide[] = null;
    export let aspectRatio: string | null = null;
    export let bgimg: string =
        (yellow &&
            "https://liveicom.azureedge.net/sinus/img/hero-wave-yellow.jpg") ||
        (gray &&
            "https://liveicom.azureedge.net/sinus/img/hero-wave-gray.jpg") ||
        "https://liveicom.azureedge.net/sinus/img/hero-wave-green.jpg";

    let overlay_color = overlay;
    if (overlay === true) overlay_color = "rgba(0,0,0, .2)";

    const [sublineBold, sublineThin] = subline.split(";");
</script>

{#if vid}
    <div class="hero">
        <div class="video-container">
            <video src={vid} autoplay muted playsinline loop />
        </div>
        <Container>
            <div class="content" class:small class:verysmall>
                {#if img}
                    <img src={img} alt="" />
                {/if}
                {#if subline}
                    <h2 class:center>
                        {@html sublineBold}
                        <span style="font-weight: 300" lang="de"
                            >{sublineThin}</span
                        >
                    </h2>
                {/if}
                {#if headline}
                    <h1 class:center lang="de">
                        {@html headline}
                    </h1>
                {/if}
            </div>
        </Container>
        {#if overlay}
            <div
                class="hero-overlay"
                style="background-color: {overlay_color};"
            />
        {/if}
        <DividerWave color={divcolor} />
    </div>
{:else if slides === null}
    <div
        class="hero"
        style="--bg-img: url('{bgimg}'); --bg-pos: {bgpos}; aspect-ratio: {aspectRatio}"
    >
        <Container>
            <div class="content" class:small class:verysmall>
                {#if img}
                    <img src={img} alt="" />
                {/if}
                {#if subline}
                    <h2 class:center>
                        {@html sublineBold}
                        <span style="font-weight: 300" lang="de"
                            >{sublineThin}</span
                        >
                    </h2>
                {/if}
                {#if headline}
                    <h1 class:center lang="de">
                        {#if animate_headline}
                            <Animate
                                jump_up
                                delay={2}
                                delay_each={0.02}
                                children="span"
                            >
                                {#each headline.split("") as char}
                                    <span
                                        >{@html char.replace(
                                            /\s/,
                                            "&nbsp;",
                                        )}</span
                                    >
                                {/each}
                            </Animate>
                        {:else}
                            {@html headline}
                        {/if}
                    </h1>
                {/if}
            </div>
        </Container>
        {#if overlay}
            <div
                class="hero-overlay"
                style="background-color: {overlay_color};"
            />
        {/if}
        <DividerWave color={divcolor} />
    </div>
{:else}
    <div class="hero" style="--bg-img: url('{bgimg}'); --bg-pos: {bgpos};">
        <Swiper
            navigation
            modules={[Autoplay, Navigation, Keyboard]}
            keyboard={{ enabled: true, onlyInViewport: true }}
            class="swiper"
            loop={slides.length > 1 ? loop : false}
            autoplay={{
                delay: 3500,
                disableOnInteraction: false,
            }}
        >
            {#each slides as { work, name, headline, person_img }}
                <div class="hero-slider" />
                <SwiperSlide>
                    <div class="hero-slide">
                        <!-- <h1 style="display:grid;place-items:center;width:100%;height:80vh">helo</h1> -->
                        <Container max="1600px">
                            <div class="content" class:small class:verysmall>
                                <h2>
                                    {name}
                                    <span style="font-weight: 300" lang="de"
                                        >{work}</span
                                    >
                                </h2>
                                <h1 lang="de">
                                    {headline || "Weil jede Sekunde zählt"}
                                </h1>
                            </div>
                        </Container>
                        <div
                            class="slide-person"
                            style:background-image={`url(${person_img})`}
                        />
                    </div>
                </SwiperSlide>
            {/each}
        </Swiper>
        <DividerWave color={divcolor} />
    </div>
{/if}

<style lang="scss">
    @import "./src/vars";
    :global(.swiper) {
        --swiper-theme-color: #{$yellow} !important;

        .slide-person {
            width: 90% !important;
            height: 90%;
            background-size: contain;
            background-position: bottom right;
            background-repeat: no-repeat;
            position: absolute;
            bottom: 0;
            right: 0;
        }
        .content {
            font-size: 5vw !important;
        }
        h2 {
            font-size: 0.4em !important;
        }
        h1 {
            font-size: 1em !important;
        }
    }
    .hero {
        --divider-height: 15vh;
        --min-height: 500px;
        --height: 80vh;
        --font-size: 3rem;
        @media (max-width: 800px) {
            --height: 60vh !important;
            --divider-height: 7vh;
            --font-size: 1.5rem;
        }
        position: relative;
        width: 100%;
        background-image: var(--bg-img);
        background-position: var(--bg-pos);
        background-size: cover;
        :global(:scope > *) {
            flex-grow: 1;
        }

        .hero-overlay {
            z-index: 0;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
        }

        img {
            max-width: 40vw;
            max-height: 100%;
            object-fit: contain;
            filter: drop-shadow(0 0 1rem rgba(black, 0.5));
            margin: auto;
            padding-bottom: 1em;
        }

        .video-container {
            width: 100%;
            height: 100%;
            position: absolute;
            overflow: hidden;
            top: 0;
            left: 0;

            video {
                position: relative;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
                // https://stackoverflow.com/questions/36679287/css-tag-object-fitcover-does-not-crop-clip-videos-in-chrome
                border-radius: 1px;
            }
        }

        .content {
            position: relative;
            z-index: 2;
            display: flex;
            height: var(--height);
            min-height: var(--min-height);
            &.small {
                --height: 60vh;
            }
            &.verysmall {
                --height: 30vh;
            }

            align-content: flex-end;
            flex-wrap: wrap;
            width: 100%;
            padding-bottom: var(--divider-height);
            font-size: var(--font-size);
            h1,
            h2 {
                font-size: 1em;
                text-align: left;
                // word-break: break-all;
                hyphens: auto;
                width: 100%;
                text-shadow: 0 0.125em 0.25em rgba(black, 0.8);
                text-transform: uppercase;
                font-size: 1.6em;
                margin: 0.25em 0;
                font-weight: bold;

                &,
                & * {
                    color: white !important;
                }
                &.center {
                    text-align: center;
                }
                span {
                    display: inline-block;
                    letter-spacing: -0.05ch;
                }
            }
            h2 {
                font-size: 0.5em;
            }
        }
    }
    :global(.divider-wave) {
        position: absolute !important;
        bottom: -2px; // flickering
        z-index: 1;
        :global(svg) {
            height: var(--divider-height) !important;
        }
        :global(path) {
            fill: var(--themeBgColor) !important;
        }
    }
</style>
